export enum Channel {
    WHATSAPP = 'WhatsApp',
    MESSENGER = 'Messenger',
    WEB = 'Web',
    APP = 'App',
    TELEGRAM = 'Telegram',
    KIOSK = 'Kiosk'
}

export enum OfferType {
    ITEM = 'item',
    FIXED_DISCOUNT = 'fixedDiscount',
    PERCENTAGE_DISCOUNT = 'percentageDiscount',
    ITEM_DISCOUNT = 'itemDiscount',
    SPECIAL = 'special'
}

export enum CouponSource {
    INTERNAL = 'internal',
    EXTERNAL = 'external',
    INDIVIDUAL = 'individual',
    LINKED = 'linked'
}

export enum CouponStatus {
    REDEEMED = 'REDEEMED',
    EXPIRED = 'EXPIRED',
    AVAILABLE = 'AVAILABLE'
}

export enum OrderType {
    IN_STORE = 'inStore',
    DELIVERY = 'delivery',
    PICKUP = 'pickup',
    DRIVETHROUGH = 'driveThru'
}

type Flag<T, U = unknown> = { value: T } & Omit<U, 'value'>

export enum DiscountInactiveReasonCode {
    MIN_ORDER_PRICE_NOT_MET = 1,
    MISSING_SPECIFIC_ITEMS = 2,
    MISSING_ITEMS_FROM_SECTION = 3,
    MAX_ORDER_PRICE_EXCEEDED = 4,
    UNKNOWN = 5 // should not be used
}

export interface Flags {
    // Server-Side Flags
    requireLogin: Flag<boolean>
    showInMyCoupons: Flag<boolean>

    // Client-Side Flags
    wallet: Flag<boolean>
    applied: Flag<boolean>
    active: Flag<boolean, { reasonCode?: DiscountInactiveReasonCode }>
}

export enum ApplyTo {
    ALL = 'all',
    NONE = 'none',
    DISHES = 'dishes',
    SECTIONS = 'sections'
}

const localeToCode = {
    en_US: 'en',
    he_IL: 'iw',
    ar_IL: 'ar',
    ru_RU: 'ru',
    pt_PT: 'pt',
    es_ES: 'es',
    fr_FR: 'fr',
    pap_AW: 'pap',
    de_DE: 'de',
    fi_FI: 'fi'
} as const

type LanguageLocale = keyof typeof localeToCode

export interface WeeklyAvailability {
    minuteOfWeek: number
    durationMins: number
}

export interface Exception {
    start: Date
    end: Date
    available: boolean
    comment: Partial<Record<LanguageLocale, string>>
}

export interface Availability {
    weekly: WeeklyAvailability[]
    exceptions: Exception[]
}

export interface InternalCoupon {
    id: string;
    code: string;
    parentCode: string;
    title: Record<string, string>;
    description: Record<string, string>;
    expiration?: Date;
    img: string;
    status: CouponStatus;
    orderTypes: OrderType[];
    channels: Channel[];
    source: CouponSource;
    offerType: OfferType;
    price: number;
    totalUses?: number;
    flags: Partial<Flags>;
    availability: Availability;
    formattedExpiration?: string;
}

export interface DiscountCoupon extends Omit<InternalCoupon, 'offerType'> {
    offerType: Exclude<OfferType, OfferType.ITEM>;
    minOrderPrice: number;
    applyTo: string;
    itemOrSectionIds: string[];
    maxOrderPrice: number;
    inactive: boolean;
}

export interface ItemCoupon extends Omit<InternalCoupon, 'offerType'> {
    offerType: OfferType.ITEM;
}

export interface IntegrationData {
    type: string
    holders: { id: string; name: string }[]
}

export type SpecialDiscount = DiscountCoupon & {
    offerType: OfferType.SPECIAL
    integrationData: IntegrationData
}

export interface ExternalCoupon extends Pick<InternalCoupon, 'id' | 'title' | 'description' | 'expiration' | 'flags' | 'img' | 'price' | 'orderTypes' | 'source' | 'channels'> {
    source: CouponSource.EXTERNAL
    sequence: number
    orderTypes: OrderType[]
}

export type ParentCoupon = ItemCoupon | DiscountCoupon | SpecialDiscount;

export interface LinkedCoupon extends Omit<ParentCoupon, 'source'> {
    source: CouponSource.LINKED;
    usesLeft: number | null;
    status: CouponStatus;
    attachedAt: Date;
}

export type Coupon = LinkedCoupon | ParentCoupon | ExternalCoupon;
export type NonExternalCoupon = Exclude<Coupon, ExternalCoupon>
